/**
 * @fileoverview Institution Stock Page
 * Displays stock inventory for the user's institution
 */

import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { formatDate } from '../../utils/formatters';
import {
    Box,
    Typography,
    Grid,
    Chip,
    Alert,
    Paper,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningIcon from "@mui/icons-material/Warning";
import { DmsPage, DmsAltBox, DmsTable, intl, fetchData, HTTP_STATUS, LanguageBaseContext } from "dmsbasereact";
import { ReportFilters } from "../ReportsPage/components/ReportFilters";
import { isExpired, isExpiringSoon } from "../../utils/expiration";

const getExpirationChip = (expirationDate) => {
    if (!expirationDate) {
        return <Chip label={intl.get("NoExpiration").d("No Expiration")} size="small" />;
    }
    if (isExpired(expirationDate)) {
        return <Chip label={intl.get("Expired").d("Expired")} color="error" size="small" />;
    }
    if (isExpiringSoon(expirationDate, 90)) {
        return <Chip label={intl.get("ExpiringSoon").d("Expiring Soon")} color="warning" size="small" />;
    }
    return <Chip label={intl.get("Valid").d("Valid")} color="success" size="small" />;
};

/**
 * InstitutionStockPage - Displays stock inventory for the user's institution
 */
export const InstitutionStockPage = () => {
    const tableRef = useRef(null);
    const { language } = useContext(LanguageBaseContext);

    const [stats, setStats] = useState({
        total_batches: 0,
        total_quantity: 0,
        expired_count: 0,
        expiring_soon_count: 0,
    });

    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});

    const filterFields = useMemo(() => [
        { name: "batch_number", label: intl.get("Batch").d("Batch"), type: "text" },
        { name: "part_name", label: intl.get("Product").d("Product"), type: "text" },
        { name: "part_number", label: intl.get("ProductCode").d("Product Code"), type: "text" },
        { name: "organization_branch_name", label: intl.get("Institution").d("Institution"), type: "text" },
        {
            name: "status",
            label: intl.get("Status").d("Status"),
            type: "multiselect",
            options: [
                { value: "valid", label: intl.get("Valid").d("Valid") },
                { value: "expiring_soon", label: intl.get("ExpiringSoon").d("Expiring Soon") },
                { value: "expired", label: intl.get("Expired").d("Expired") },
            ],
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    ], [language]);

    const buildPreFilter = useCallback(() => {
        return Object.entries(appliedFilters)
            .filter(([, value]) => value !== "" && value !== null && value !== undefined)
            .map(([name, value]) => ({ name, value: Array.isArray(value) ? value.join(",") : String(value) }));
    }, [appliedFilters]);

    const fetchStats = useCallback(async () => {
        try {
            const preFilter = buildPreFilter();
            const filterParam = preFilter.length > 0 ? JSON.stringify(preFilter) : "[]";
            const response = await fetchData(
                `/batch_location/my-institutions/stats?filter=${encodeURIComponent(filterParam)}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                setStats(response.data.data);
            }
        } catch (err) {
            console.error("Error fetching stock stats:", err);
        }
    }, [buildPreFilter]);

    useEffect(() => {
        fetchStats();
        tableRef.current?.updateTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    const expiredCount = stats.expired_count;
    const expiringSoonCount = stats.expiring_soon_count;
    const totalQuantity = stats.total_quantity;
    const totalBatches = stats.total_batches;

    const pageName = intl.get("InstitutionStock").d("Institution Stock");

    const header = [
        { label: intl.get("Batch").d("Batch"), column_name: "batch_number" },
        { label: intl.get("Institution").d("Institution"), column_name: "organization_branch_name" },
        { label: intl.get("Product").d("Product"), column_name: "part_name" },
        { label: intl.get("ProductCode").d("Product Code"), column_name: "part_number" },
        {
            label: intl.get("Quantity").d("Quantity"),
            column_name: "quantity",
            element: (row) => (
                <Typography variant="body2" fontWeight="bold">{row.quantity || 0}</Typography>
            ),
        },
        {
            label: intl.get("Expiration").d("Expiration"),
            column_name: "expiration",
            type: "date",
            element: (row) => formatDate(row.expiration),
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            sortable: false,
            element: (row) => getExpirationChip(row.expiration),
        },
    ];

    return (
        <DmsPage name={pageName}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center" gap={1}>
                            <InventoryIcon color="primary" />
                            <Typography variant="h6">
                                {intl.get("InstitutionStock").d("Institution Stock")}
                            </Typography>
                        </Box>
                        <ReportFilters
                            filterFields={filterFields}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onApply={() => setAppliedFilters({ ...filters })}
                            onClear={() => { setFilters({}); setAppliedFilters({}); }}
                            onRefresh={() => { fetchStats(); tableRef.current?.updateTable(); }}
                            showDateRange={false}
                        />
                    </Box>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography variant="h4" color="primary.main" fontWeight="bold">
                                    {totalBatches}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("TotalBatches").d("Total Batches")}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography variant="h4" color="info.main" fontWeight="bold">
                                    {totalQuantity}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("TotalUnits").d("Total Units")}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: expiringSoonCount > 0 ? "warning.main" : "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    color={expiringSoonCount > 0 ? "warning.main" : "textSecondary"}
                                    fontWeight="bold"
                                >
                                    {expiringSoonCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("ExpiringSoon").d("Expiring Soon")}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: expiredCount > 0 ? "error.main" : "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    color={expiredCount > 0 ? "error.main" : "textSecondary"}
                                    fontWeight="bold"
                                >
                                    {expiredCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("Expired").d("Expired")}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {(expiredCount > 0 || expiringSoonCount > 0) && (
                        <Box sx={{ mb: 2 }}>
                            {expiredCount > 0 && (
                                <Alert severity="error" sx={{ mb: 1 }}>
                                    {intl.get("ExpiredBatchesAlert", { count: expiredCount })
                                        .d("{count} batch(es) have expired")}
                                </Alert>
                            )}
                            {expiringSoonCount > 0 && (
                                <Alert severity="warning" icon={<WarningIcon />}>
                                    {intl.get("ExpiringSoonBatchesAlert", { count: expiringSoonCount })
                                        .d("{count} batch(es) expiring within 90 days")}
                                </Alert>
                            )}
                        </Box>
                    )}

                    <DmsTable
                        ref={tableRef}
                        header={header}
                        url="/batch_location/my-institutions/table"
                        managed={true}
                        needs_authentication={true}
                        pre_filter={buildPreFilter()}
                        toolbar={<></>}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

InstitutionStockPage.propTypes = {};
